import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { driverService } from '../../../../../application/DriverService';
import { Driver } from '../../../../../domain/driver/Driver';
import useErrorMiddleware from '../../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useShowSnackbarMessage } from '../../../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { StopMapper } from '../../../../../mappers/stop/StopMapper';
import { PlanDetailsActionType } from '../../context/actions';
import { usePlanDetailsContext } from '../../context/context';

type DriverStopsExchangeProps = { driverA: Driver; driverB: Driver };

type UseExchangeDriverStopsResponse = ServiceResponse<({ driverA, driverB }: DriverStopsExchangeProps) => void, void>;

// Hooks act as 'Adapter' layer.
export const useExchangeDriverStops = (): UseExchangeDriverStopsResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { planDetailsState, planDetailsDispatch } = usePlanDetailsContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const queryClient = useQueryClient();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    async ({ driverA, driverB }: DriverStopsExchangeProps): Promise<void> => {
      await driverService.saveStops({ driver: driverA, project: activeProject! });
      await driverService.saveStops({ driver: driverB, project: activeProject! });
    },
    {
      onMutate: ({ driverA, driverB }: DriverStopsExchangeProps) => {
        // Optimistically update to the new value
        const newDriversMap = { ...planDetailsState.driversMap };
        newDriversMap[driverA.id] = driverA;
        newDriversMap[driverB.id] = driverB;

        const newStopsMap = { ...planDetailsState.stopsMap };
        driverA.stops.forEach((stop) => {
          newStopsMap[stop.id] = stop;
        });
        driverB.stops.forEach((stop) => {
          newStopsMap[stop.id] = stop;
        });

        planDetailsDispatch({
          type: PlanDetailsActionType.SET_ACTIVE_PLAN_DRIVERS,
          payload: {
            planDrivers: Object.values(newDriversMap),
          },
        });

        planDetailsDispatch({
          type: PlanDetailsActionType.SET_ACTIVE_PLAN_STOPS,
          payload: {
            planStops: Object.values(newStopsMap),
          },
        });

        queryClient.cancelQueries([`planner_active_plan_drivers`, planDetailsState.plan?.id]);
        queryClient.cancelQueries([`planner_active_plan_stops`, planDetailsState.plan?.id]);

        queryClient.setQueryData(
          [`planner_active_plan_drivers`, planDetailsState.plan?.id],
          Object.values(newDriversMap)
        );
        queryClient.setQueryData([`planner_active_plan_stops`, planDetailsState.plan?.id], Object.values(newStopsMap));
      },
      onError: (err: any) => {
        console.info(`Update Driver Stops Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        queryClient.invalidateQueries([`planner_active_plan_drivers`, planDetailsState.plan?.id]);
        queryClient.invalidateQueries([`planner_active_plan_stops`, planDetailsState.plan?.id]);

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  const exchangeDrivers = ({ driverA, driverB }: DriverStopsExchangeProps) => {
    if (!activeProject || !activeProject.custom_fields) {
      return;
    }

    // I pass it to DTO and then to Domain to create new objects and don't have problems with references
    const stopsA = driverA.stops.map(StopMapper.toDTO);
    const stopsB = driverB.stops.map(StopMapper.toDTO);

    driverA.stops = stopsB.map((stop) => StopMapper.toDomain(stop, activeProject.custom_fields!));
    driverB.stops = stopsA.map((stop) => StopMapper.toDomain(stop, activeProject.custom_fields!));

    mutate({ driverA, driverB });
  };

  return {
    run: exchangeDrivers,
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
  };
};
